import React from 'react';
import { BroadcastEventType } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import { toast } from '@cotera/client/app/components/ui';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';

/**
 * Replaces a dataset's contents with a CSV.
 *
 * Nothing is patched locally: the new artifact arrives through the resource
 * refresh and the preview is rebuilt from it. The event is emitted locally too,
 * so the page does not wait on the server broadcast round trip.
 *
 * Rejects on failure, so a control can keep showing its busy state until the
 * import has actually landed.
 */
export const useImportDatasetCsv = (
  datasetId: string | null
): ((file: File) => Promise<void>) => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  return React.useCallback(
    async (file: File) => {
      if (datasetId === null) {
        return;
      }
      const result = await client.resource.dataset.importCsv({
        id: datasetId,
        file,
      });
      if (result.isErr()) {
        const message =
          result.error.t === 'application'
            ? result.error.err.message ?? 'Could not import CSV'
            : 'Could not import CSV';
        toast.error(message);
        throw new Error(message);
      }
      toast.success('CSV imported');
      emit(BroadcastEventType.ResourceUpdated, {
        orgId,
        resourceType: 'dataset',
        resourceId: datasetId,
      });
    },
    [client, datasetId, orgId]
  );
};
